import axios from 'axios'
import { BASE_URL, BASE_URL_API } from '../Constants'

const apiClient = axios.create({
  baseURL: BASE_URL_API,
  withCredentials: true, // 1. FIX: Send cookies with EVERY apiClient request
})

let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      
      // Prevent loop if the refresh request itself fails
      if (originalRequest.url.includes('/auth/refresh')) {
        return Promise.reject(error)
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return apiClient(originalRequest)
          })
          .catch((err) => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const refreshResponse = await axios.post(
          `${BASE_URL}/login/auth/refresh`,
          {},
          {
            withCredentials: true, // Sends HTTP-only refresh cookie
          }
        )

        const newAccessToken = refreshResponse.data.token 
        
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`

        processQueue(null, newAccessToken)
        
        return apiClient(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export default apiClient


export async function request(url, options = {}) {
  try {
    const response = await apiClient({
      url,
      ...options,
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export async function get(url, params, extraOptions = {}) {
  const options = {
    method: 'GET',
    ...extraOptions,
  }

  if (params && typeof params === 'object') {
    const flatParams = {}

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (typeof value === 'object' && !Array.isArray(value)) {
          Object.entries(value).forEach(([subKey, subValue]) => {
            if (subValue !== undefined && subValue !== null && subValue !== '') {
              flatParams[`${key}[${subKey}]`] = subValue
            }
          })
        } else {
          flatParams[key] = value
        }
      }
    })

    const paramsString = new URLSearchParams(flatParams).toString()
    url += paramsString ? `?${paramsString}` : ''
  }

  return request(url, options)
}

export async function post(url, body = {}, extraOptions = {}) {
  const options = {
    method: 'POST',
    headers: {
      'Content-Type':
        body instanceof FormData
          ? 'multipart/form-data'
          : 'application/json',
      ...extraOptions.headers,
    },
    data: body,
    ...extraOptions,
  }

  return request(url, options)
}

export async function put(url, body, extraOptions = {}) {
  const options = {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...extraOptions.headers,
    },
    data: body,
    ...extraOptions,
  }

  return request(url, options)
}

export async function del(url, extraOptions = {}) {
  const options = {
    method: 'DELETE',
    ...extraOptions,
  }

  return request(url, options)
}

export async function patch(url, body, extraOptions = {}) {
  const options = {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...extraOptions.headers,
    },
    data: body,
    ...extraOptions,
  }

  return request(url, options)
}

export function setupAxios(axiosInstance) {
  const baseUrl = import.meta.env.VITE_APP_API_URL
  axiosInstance.defaults.baseURL = baseUrl
  axiosInstance.defaults.headers.Accept = 'application/json'
}